"use client";

import { Box, Flex, Stack, Text } from "@chakra-ui/react";
import { useState, type ReactElement } from "react";
import { LuInfo } from "react-icons/lu";
import type { PublicDestinationRevenue } from "@otalytics/shared";

interface DestinationRevenueProps {
  revenue: PublicDestinationRevenue;
  destinationName: string;
}

/**
 * Estimated weekly turnover for the first page.
 *
 * The only figure on this report that is not directly observed, so it is the
 * only one that carries a methodology panel. Three rules hold it honest:
 *
 *   1. The word "estimated" is in the label, not buried in the small print.
 *   2. The coverage line sits directly under the number, always — the figure
 *      may never appear without saying how much of the page it covers.
 *   3. The method is stated in plain language, in the same four points the
 *      paid dashboard uses, so both surfaces explain the number identically.
 *
 * Deliberately NOT claimed: an accuracy percentage. The dashboard quotes one;
 * it is unsourced in this repo, and a specific falsifiable statistic on an
 * ungated marketing page needs evidence behind it before it goes out.
 */
export function DestinationRevenue({
  revenue,
  destinationName,
}: DestinationRevenueProps): ReactElement {
  const [showMethod, setShowMethod] = useState(false);

  const weeks = revenue.weeks;

  return (
    // This is the only block on the page that breaks the card language, and
    // deliberately: it is the one figure here that is estimated rather than
    // observed, and the one number the whole page is built to deliver. It
    // earns the page's single full-measure statement treatment — framed by
    // rules top and bottom rather than boxed like the surrounding tiles.
    <Box
      py={{ base: 8, md: 10 }}
      borderTop="1px solid var(--color-border)"
      borderBottom="1px solid var(--color-border)"
    >
      <Text
        fontFamily="var(--font-geist-mono)"
        fontSize="11px"
        letterSpacing="0.04em"
        color="var(--color-text-tertiary)"
        mb={3}
      >
        estimated weekly turnover
      </Text>

      <Flex alignItems="baseline" gap={2} flexWrap="wrap">
        <Text
          className="tabular-nums"
          fontFamily="var(--font-display)"
          fontSize={{ base: "40px", md: "48px" }}
          fontWeight="600"
          letterSpacing="-0.03em"
          lineHeight="1.05"
          color="var(--color-text-primary)"
        >
          €{Math.round(revenue.avgWeeklyRevenueEur).toLocaleString("en-GB")}
        </Text>
        <Text fontSize="15px" color="var(--color-text-secondary)">
          a week
        </Text>
      </Flex>

      {/* Never rendered conditionally: the denominator travels with the
          figure, or the figure overstates what it measured. The fraction
          carries the same weight as the money — set in plain body text it
          read as a footnote, which shouts the number's precision while
          murmuring its partiality. */}
      <Text fontSize="14px" color="var(--color-text-secondary)" mt={3} lineHeight="1.6">
        Across{" "}
        <Text
          as="span"
          className="tabular-nums"
          fontWeight="600"
          color="var(--color-text-primary)"
        >
          {revenue.toursWithSignal} of {revenue.toursOnPage} tours
        </Text>{" "}
        on the first page of {destinationName} with enough review movement to
        estimate from — about{" "}
        <Text as="span" className="tabular-nums" color="var(--color-text-primary)">
          €{Math.round(revenue.totalEstimatedRevenueEur).toLocaleString("en-GB")}
        </Text>{" "}
        over {weeks} complete {weeks === 1 ? "week" : "weeks"}.
      </Text>

      <Box mt={4} pt={4} borderTop="1px solid var(--color-border-subtle)">
        <Flex alignItems="center" gap={2}>
          <LuInfo
            size={13}
            color="var(--color-text-tertiary)"
            style={{ flexShrink: 0 }}
          />
          <Text fontSize="12px" color="var(--color-text-tertiary)" lineHeight="1.5">
            <Text as="span" fontWeight="600" color="var(--color-text-secondary)">
              This one is an estimate
            </Text>{" "}
            — every other number on this page was scraped.{" "}
            <Text
              as="span"
              cursor="pointer"
              color="var(--color-text-secondary)"
              textDecoration="underline"
              textDecorationColor="var(--color-border)"
              textUnderlineOffset="2px"
              _hover={{ color: "var(--color-text-primary)" }}
              onClick={() => setShowMethod(!showMethod)}
            >
              {showMethod ? "Hide details" : "How is this calculated?"}
            </Text>
          </Text>
        </Flex>

        {showMethod && (
          <Stack
            gap="6px"
            fontSize="12px"
            color="var(--color-text-tertiary)"
            lineHeight="1.6"
            mt={3}
          >
            <Text>
              &bull; Not every customer leaves a review &mdash; we estimate the
              true number of bookings from the fraction that do
            </Text>
            <Text>
              &bull; Each booking typically covers more than one person, so we
              account for average group size
            </Text>
            <Text>
              &bull; Reviews appear a few days after the experience &mdash;
              revenue is backdated accordingly
            </Text>
            <Text>
              &bull; The listed &ldquo;from&rdquo; price closely matches what
              customers actually pay on average
            </Text>
          </Stack>
        )}
      </Box>
    </Box>
  );
}
